import { createContext, useContext } from 'react';
import type { ConnectivityStatus } from './connectivity.js';
import type { QueuedWrite } from './db.js';
import type { EnqueueInput, FlushResult, WriteQueueSnapshot } from './write-queue.js';

/**
 * What every screen can ask about the connection and the writes waiting on it. Feature
 * modules never touch the queue or the connectivity store directly — they read this.
 */
export interface OfflineContextValue {
  status: ConnectivityStatus;
  /** True whenever a request would not get through right now. */
  isOffline: boolean;
  pending: readonly QueuedWrite[];
  failed: readonly QueuedWrite[];
  pendingCount: number;
  failedCount: number;
  flushing: boolean;
  enqueue: (input: EnqueueInput) => Promise<QueuedWrite>;
  flush: () => Promise<FlushResult>;
  discard: (seq: number) => Promise<void>;
  retry: (seq: number) => Promise<void>;
}

export const emptyQueueSnapshot: WriteQueueSnapshot = {
  pending: [],
  failed: [],
  pendingCount: 0,
  failedCount: 0,
  flushing: false,
};

export const OfflineContext = createContext<OfflineContextValue | null>(null);

export function useOffline(): OfflineContextValue {
  const value = useContext(OfflineContext);
  if (!value) {
    throw new Error('useOffline must be used inside <OfflineProvider>');
  }
  return value;
}
